import { useEffect } from 'react';
import { Box, Button, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { School, Plus, Edit, Building2, Settings } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSuperAdminSchoolStore } from '@/store/superAdmin/schoolStore';
import { useOrganizationStore } from '@/store/superAdmin/organizationStore';
import { PageHeader } from '@/components/navigation/PageHeader';
import { tokens } from '@/theme/tokens';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';

export function GlobalSchoolListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('orgId');
  const { globalSchools, loading: schoolsLoading, error: schoolsError, fetchAllSchools, fetchSchoolsByOrg } = useSuperAdminSchoolStore();
  const { organizations, fetchOrganizations, loading: orgsLoading } = useOrganizationStore();

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (orgId) {
      fetchSchoolsByOrg(orgId);
    } else {
      fetchAllSchools();
    }
  }, [orgId, fetchAllSchools, fetchSchoolsByOrg]);

  const loading = schoolsLoading || orgsLoading;
  const error = schoolsError;

  if (loading && globalSchools.length === 0) return <Loader message="Loading schools..." />;
  if (error) return <ErrorState message={error} onRetry={() => orgId ? fetchSchoolsByOrg(orgId) : fetchAllSchools()} />;

  const columns = [
    { 
      field: 'school_name', 
      headerName: 'School Name', 
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: tokens.radius.sm,
              backgroundColor: tokens.colors.info[50],
              color: tokens.colors.info.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <School size={16} />
          </Box>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ color: tokens.colors.text.primary, display: 'block' }}>
              {params.value || 'Unnamed School'}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: tokens.colors.text.secondary, display: 'block' }}>
              {params.row.email && params.row.email !== 'user@example.com' ? params.row.email : ''}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { 
      field: 'school_code', 
      headerName: 'School Code', 
      flex: 0.8, 
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          {params.value && params.value !== 'string' ? params.value : '-'}
        </Typography>
      )
    },
    { 
      field: 'organization_id', 
      headerName: 'Organization', 
      flex: 1.3, 
      minWidth: 220,
      renderCell: (params) => {
        const org = organizations.find((o) => o.id === params.value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: tokens.colors.text.secondary, overflow: 'hidden', width: '100%' }}>
            <Building2 size={16} style={{ flexShrink: 0 }} />
            <Typography variant="body2" noWrap sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {org ? (org.organization_name || org.name) : 'Unknown'}
            </Typography>
          </Box>
        );
      }
    },
    { 
      field: 'phone', 
      headerName: 'Phone', 
      flex: 0.9, 
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value && params.value !== 'string' ? params.value : '-'}
        </Typography>
      )
    },
    { field: 'city', headerName: 'City', flex: 0.8, minWidth: 100, renderCell: (params) => params.value && params.value !== 'string' ? params.value : '-' },
    { field: 'state', headerName: 'State', flex: 0.8, minWidth: 110, renderCell: (params) => params.value && params.value !== 'string' ? params.value : '-' },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 0.8, 
      minWidth: 90,
      renderCell: (params) => (
        <Chip
          label={params.value === 'ACTIVE' || params.value === 'active' ? 'Active' : 'Inactive'}
          size="small"
          sx={{
            backgroundColor: params.value === 'ACTIVE' || params.value === 'active' ? tokens.colors.success[50] : tokens.colors.error[50],
            color: params.value === 'ACTIVE' || params.value === 'active' ? tokens.colors.success.main : tokens.colors.error.main,
            fontWeight: 600,
            borderRadius: tokens.radius.sm,
          }}
        />
      ),
    },
    { 
      field: 'created_at', 
      headerName: 'Created Date', 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        if (!params.value) return '-';
        return (
          <Typography variant="body2" color="text.secondary">
            {new Date(params.value).toLocaleDateString()}
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
          <Tooltip title="Configure School">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/super-admin/schools/configure/${params.row.id}`);
              }}
              sx={{ color: tokens.colors.secondary.main }}
            >
              <Settings size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit School">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/super-admin/schools/edit/${params.row.id}`);
              }}
              sx={{ color: tokens.colors.primary.main }}
            >
              <Edit size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title={orgId ? "Organization Schools" : "Global Schools"}
        subtitle="Manage all schools registered on the platform."
        actions={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => navigate('/super-admin/schools/add' + (orgId ? `?orgId=${orgId}` : ''))}
            sx={{
              borderRadius: tokens.radius.sm,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
            }}
          >
            Create School
          </Button>
        }
      />

      <Box sx={{ width: '100%', backgroundColor: tokens.colors.card, borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, overflow: 'hidden' }}>
        <DataGrid
          rows={globalSchools}
          columns={columns}
          autoHeight
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          onRowClick={(params) => navigate(`/super-admin/schools/${params.row.id}`)}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 100 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderColor: tokens.colors.border,
              display: 'flex',
              alignItems: 'center',
              py: 1.5,
              outline: 'none !important',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: tokens.colors.grey[50],
              borderBottom: `1px solid ${tokens.colors.border}`,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: tokens.colors.grey[50],
              cursor: 'pointer',
            },
          }}
        />
      </Box>
    </Box>
  );
}
